"use client";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";

import usePosition from "@/hooks/usePosition";
import {
  embeddedSearchQuerySubmittedEvent,
  headerSearchBarSubmittedEvent,
} from "@/lib/segment";
import defaultSuggestions from "@/mocks/defaultSuggestions.json";

import ProductSearchSuggestions from "./ProductSearchSuggestions";
import { ProductSearchContextProvider } from "./ProductSearchContextProvider";
import SearchBox from "./SearchBox";
import { Suggestions } from "./types";
import {
  EventCacheType,
  getCourseSubject,
  getProgramSubjectForTagular,
  resetEventCache,
  getWebContext,
} from "@/utils/tagular/helpers";
import { ProcessedCourse } from "@/common/course/types";
import { Program } from "@/common/program/types";
import { PacingType } from "@/common/program/types";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { useInView } from "react-intersection-observer";
import useFormEvents from "@/hooks/eventing/useFormtEvents";
import { FieldInputted } from "@/hooks/eventing/types/redventures.usertracking.v3.FieldInputted";
import { getCorrelationID } from "@/utils/tagular/helpers";
import { getPageType } from "@/utils/tagular/helpers";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import { createSearchQueryId } from "@/utils/getSearchQueryId";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import useProductSearch from "@/monarch/rules/productSearch/useProductSearch";
import ProductSuggestionsV2 from "./components/v2/ProductSuggestions";

export interface ProductSearchProps {
  placeholder: string;
  button?: string;
  suggestions?: Suggestions;
  variant?: string;
  embedded?: boolean;
  showButtons?: boolean;
  isRoundedButton?: boolean;
  isSearchBoxColumn?: boolean;
  course?: ProcessedCourse;
  program?: Program;
  conversionType?: string;
  location?: string;
  autoFocus?: boolean;
  searchButtonColor?: string;
  searchButtonLabelColor?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

export interface sendAnalyticsProps {
  fromViewAllButton?: boolean;
  input?: string | undefined;
}

const ProductSearch = ({
  searchButtonColor,
  searchButtonLabelColor,
  placeholder,
  button = "Search",
  suggestions = defaultSuggestions,
  variant = "",
  embedded = true,
  showButtons = true,
  isRoundedButton = true,
  isSearchBoxColumn = true,
  course,
  program,
  conversionType,
  location = "embedded-search",
  autoFocus = false,
  onFocus,
  onBlur,
}: ProductSearchProps) => {
  const params = useSearchParams();
  const [showHits, setShowHits] = useState(false);
  // Track if this is the first focus event
  const isFirstFocus = useRef(true);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const positionedDivRef = useRef<HTMLDivElement | null>(null);
  const position = usePosition(searchBoxRef, positionedDivRef);
  const hasViewedRef = useRef(false);

  // get referrer URL
  let referrerUrl = "";
  if (typeof document !== "undefined" && document.referrer) {
    referrerUrl = document.referrer;
  }

  const { flagValue, isLoading: isFlagLoading } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_SEARCH_BAR_REVAMP_TEST",
    "legacy",
  );

  const isTestFlag = flagValue === "test";

  const {
    isLoading: isMonarchLoading,
    variant: monarchVariant,
    data: monarchData,
  } = useProductSearch();

  const webElement: ElementClicked["webElement"] = {
    elementType: ElementType.Entry,
    htmlId: "search-button",
    location: location,
    text: "search",
    name: isTestFlag ? `${location}-button` : (conversionType ?? "hero-search"),
  };

  const { elementClicked, elementViewed } = useElementEvents({ webElement });
  const { fieldInputted } = useFormEvents({});
  const handleFocus = () => {
    // Skip first focus if triggered via autoFocus
    if (isFirstFocus.current && autoFocus) {
      isFirstFocus.current = false;
      return;
    }

    resetEventCache(EventCacheType.ProductSearch);
    setShowHits(true);
    isFirstFocus.current = false;

    // Call external onFocus callback if provided
    onFocus?.();
  };

  const { ref: inViewEventRef, inView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  const handleBlur = () => {
    setShowHits(false);

    // Call external onBlur callback if provided
    onBlur?.();
  };

  const sendAnalytics = (data?: sendAnalyticsProps) => {
    const searchQueryId = createSearchQueryId();

    const customDimensions = createCustomDimensions([
      ["search_query_id", searchQueryId],
      ["search_query", data?.input || ""],
    ]);

    elementClicked({
      customDimensions,
    });

    const handler = embedded
      ? embeddedSearchQuerySubmittedEvent
      : headerSearchBarSubmittedEvent;

    if (typeof window !== "undefined" && data?.input) {
      const webContextData = getWebContext();
      const fieldInputtedEvent: FieldInputted = {
        correlationId: getCorrelationID(),
        userInputField: {
          fieldName: location,
          fieldLabel: placeholder,
          fieldType: "search-bar",
          fieldValue: data?.input,
        },
        webContext: {
          ...webContextData,
          userAgent: webContextData?.userAgent ?? undefined,
          page: {
            referrer: referrerUrl,
            title: document.title,
            url: window.location.href,
            pageType: getPageType(),
          },
        },
      };
      fieldInputted(fieldInputtedEvent);
    }

    handler({
      label: searchBoxRef.current?.querySelector("input")?.value,
      fromViewAllButton: data?.fromViewAllButton,
      courseUuid: course?.productUuid,
      activeCourseRunKey: course?.activeCourseRun?.key,
      ...(course && {
        isSelfPaced:
          course.activeCourseRun?.pacingType === PacingType.SELFPACED,
      }),
      subject: program
        ? getProgramSubjectForTagular(program)
        : getCourseSubject(course),
      ...(program && {
        programUuid: program.externalId,
        path: program.marketingUrl,
      }),
    });
  };

  const LegacySearchBox = (
    <div>
      <div ref={searchBoxRef}>
        <SearchBox
          button={button}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          variant={variant}
          onSubmit={sendAnalytics}
          showButtons={showButtons}
          isRoundedButton={isRoundedButton}
          isSearchBoxColumn={isSearchBoxColumn}
          autoFocusOnMount={autoFocus}
          searchButtonColor={searchButtonColor}
          searchButtonLabelColor={searchButtonLabelColor}
          location={location}
        />
      </div>
      <div
        className={`${
          showHits ? "my-1" : "hidden"
        } px-3 md:px-0 md:w-[504px] absolute z-50 ${
          position === "above" ? "bottom-full" : ""
        }`}
        ref={positionedDivRef}
        onMouseDown={(e) => e.preventDefault()}
      >
        <ProductSearchSuggestions
          suggestions={suggestions}
          sendAnalytics={sendAnalytics}
          location={location}
        />
      </div>
    </div>
  );

  // Revamp search box - test variant
  const RevampSearchBox = (
    <div className="relative max-w-[633px]" ref={searchBoxRef}>
      {/* Input Search Box */}
      <SearchBox
        button={button}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="Search for programs, courses, or topics..."
        variant="revamp"
        onSubmit={sendAnalytics}
        showButtons={true}
        isRoundedButton={isRoundedButton}
        isSearchBoxColumn={isSearchBoxColumn}
        autoFocusOnMount={autoFocus}
        searchButtonColor={searchButtonColor}
        searchButtonLabelColor={searchButtonLabelColor}
        location={location}
      />
      <div
        className={`${
          showHits ? "my-1" : "hidden"
        } w-full absolute left-0 z-50 ${position === "above" ? "bottom-full" : ""}`}
        ref={positionedDivRef}
        onMouseDown={(e) => e.preventDefault()}
      >
        {/* Search Suggestions based on the Search Box Input */}
        <ProductSuggestionsV2
          featuredPrograms={monarchData?.featuredPrograms}
          featuredCategories={monarchData?.featuredCategories}
          location={location}
        />
      </div>
    </div>
  );

  const SearchBoxSkeleton = () => {
    return (
      <div className="md:w-72">
        <div className="w-full h-12 bg-putty-100 rounded-default-md animate-pulse" />
      </div>
    );
  };

  const renderContent = () => {
    // Both flags are loading - show skeleton
    if (isFlagLoading || (isTestFlag && isMonarchLoading)) {
      return <SearchBoxSkeleton />;
    }

    // Feature flag resolved to "legacy" - show legacy immediately
    if (!isTestFlag) {
      return LegacySearchBox;
    }

    // Feature flag is "test" and Monarch resolved to "test" - show revamp
    if (monarchVariant === "test") {
      return RevampSearchBox;
    }

    // Feature flag is "test" but Monarch resolved to "control" - show legacy
    return LegacySearchBox;
  };

  return (
    <ProductSearchContextProvider query={params.get("q") || ""}>
      <div ref={inViewEventRef} className="relative min-h-12">
        {renderContent()}
      </div>
    </ProductSearchContextProvider>
  );
};

export default ProductSearch;
